import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, Button, Select, Table, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import type { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import moment from 'moment';
import { faClock, faHandHoldingDollar, faPiggyBank } from '@fortawesome/free-solid-svg-icons';

import type {
  AgentCommissionStatus,
  IAgentCommission,
  IAgentCommissionsSummary,
} from '@share/common-types';
import { getAgentCommissionsAsync, getAgentCommissionsSummaryAsync } from '@share/api';
import { Routes } from '@share/constants';
import { getUserWallet } from '@share/store/slices';
import { renderNumber, RootState, Toaster } from '@share/utils';

import {
  DashboardReportHeaderItem,
  HeaderItemColor,
  HeaderItemType,
} from '../dashboard-report/dashboard-report-header/dashboard-report-header-item';

import './style.scss';

const PAGE_SIZE = 10;
const YEARS_BACK = 3;
const ALL_YEARS = 0;
const DATE_FORMAT = 'MM-DD-yyyy';

const STATUS_COLORS: Record<AgentCommissionStatus, string> = {
  Ready: 'green',
  Pending: 'gold',
  PaidOut: 'default',
};

const STATUS_LABELS: Record<AgentCommissionStatus, string> = {
  Ready: 'agent.commissions.status.ready',
  Pending: 'agent.commissions.status.pending',
  PaidOut: 'agent.commissions.status.paid_out',
};

const money = (value: number | undefined) => `$ ${renderNumber(value, 2)}`;

/**
 * The travel agent's own commissions: what can be cashed out, what is still pending, and what was
 * already paid, one row per booking they made for a client.
 */
export function DashboardCommissions() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { account, userWallet, userWalletData } = useSelector((state: RootState) => state.loginStore);
  const available = userWalletData?.commissionBalance ?? 0;

  // The wallet in the store is the one loaded at login; reload it so the available commissions
  // reflect credits and withdrawals made since.
  useEffect(() => {
    if (userWallet) {
      dispatch(getUserWallet(userWallet));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentYear = moment().year();
  const [year, setYear] = useState<number>(currentYear);
  const [summary, setSummary] = useState<IAgentCommissionsSummary | null>(null);
  const [rows, setRows] = useState<IAgentCommission[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({ current: 1, pageSize: PAGE_SIZE, total: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const errorMessage = intl.formatMessage({ id: 'agent.commissions.error' });

  useEffect(() => {
    getAgentCommissionsSummaryAsync()
      .then((response) => setSummary(response.data))
      .catch(() => Toaster.error(errorMessage));
  }, [errorMessage]);

  const loadRows = useCallback(
    (page: number, selectedYear: number) => {
      setIsLoading(true);
      getAgentCommissionsAsync({
        page,
        results: PAGE_SIZE,
        year: selectedYear === ALL_YEARS ? undefined : selectedYear,
      })
        .then((response) => {
          setRows(response.data?.items ?? []);
          setPagination({ current: page, pageSize: PAGE_SIZE, total: response.data?.totalResults ?? 0 });
        })
        .catch(() => Toaster.error(errorMessage))
        .finally(() => setIsLoading(false));
    },
    [errorMessage],
  );

  useEffect(() => {
    loadRows(1, year);
  }, [year, loadRows]);

  const yearOptions = useMemo(
    () => [
      ...Array.from({ length: YEARS_BACK }, (_, index) => ({
        value: currentYear - index,
        label: String(currentYear - index),
      })),
      { value: ALL_YEARS, label: intl.formatMessage({ id: 'agent.commissions.filter.all' }) },
    ],
    [currentYear, intl],
  );

  const columns: ColumnsType<IAgentCommission> = [
    {
      title: <FormattedMessage id="agent.commissions.table.booking" />,
      key: 'booking',
      render: (_, row) => (
        <div className="dashboard-commissions__booking">
          <span className="dashboard-commissions__booking-name">{row.propertyName ?? '-'}</span>
          <span className="dashboard-commissions__booking-detail">
            {[row.leadName, row.customerLocator].filter(Boolean).join(' · ')}
          </span>
        </div>
      ),
    },
    {
      title: <FormattedMessage id="agent.commissions.table.travel_date" />,
      dataIndex: 'checkIn',
      key: 'checkIn',
      // The check-in is a calendar date: shown as sent, never shifted to the viewer's time zone.
      render: (value: string) => moment.parseZone(value).format(DATE_FORMAT),
    },
    {
      title: <FormattedMessage id="agent.commissions.table.total" />,
      dataIndex: 'bookingTotal',
      key: 'bookingTotal',
      align: 'right',
      render: (value: number) => money(value),
    },
    {
      title: <FormattedMessage id="agent.commissions.table.status" />,
      dataIndex: 'status',
      key: 'status',
      render: (value: AgentCommissionStatus) => (
        <Tag color={STATUS_COLORS[value]}>
          <FormattedMessage id={STATUS_LABELS[value]} />
        </Tag>
      ),
    },
    {
      title: <FormattedMessage id="agent.commissions.table.commission" />,
      dataIndex: 'commission',
      key: 'commission',
      align: 'right',
      render: (value: number) => <strong>{money(value)}</strong>,
    },
  ];

  return (
    <div className="dashboard-commissions">
      <div className="dashboard-commissions__cards">
        <DashboardReportHeaderItem
          type={HeaderItemType.CURRENCY}
          title="agent.commissions.ready"
          icon={faHandHoldingDollar}
          value={summary?.ready}
          color={HeaderItemColor.GREEN}
        />
        <DashboardReportHeaderItem
          type={HeaderItemType.CURRENCY}
          title="agent.commissions.pending"
          icon={faClock}
          value={summary?.pending}
          color={HeaderItemColor.BLUE}
        />
        <DashboardReportHeaderItem
          type={HeaderItemType.CURRENCY}
          title="agent.commissions.paid_out_year"
          icon={faPiggyBank}
          value={summary?.paidOutThisYear}
          color={HeaderItemColor.BLUE}
        />
      </div>

      <div className="dashboard-commissions__withdraw">
        <div>
          <span className="dashboard-commissions__withdraw-label">
            <FormattedMessage id="agent.commissions.withdraw.available" />
          </span>
          <span className="dashboard-commissions__withdraw-value">{money(available)}</span>
          <p className="dashboard-commissions__withdraw-hint">
            <FormattedMessage id="agent.commissions.withdraw.hint" />
          </p>
        </div>
        {available > 0 ? (
          <Link to={`/${account?.name}${Routes.WalletCashOut}`}>
            <Button type="primary">
              <FormattedMessage id="agent.commissions.withdraw.button" />
            </Button>
          </Link>
        ) : null}
      </div>

      <Alert
        className="dashboard-commissions__note"
        type="info"
        showIcon
        message={<FormattedMessage id="agent.commissions.note" />}
      />

      <div className="dashboard-commissions__list">
        <div className="dashboard-commissions__list-header">
          <h3>
            <FormattedMessage id="agent.commissions.list.title" />
          </h3>
          <Select value={year} options={yearOptions} onChange={setYear} />
        </div>

        <Table
          rowKey="bookingId"
          columns={columns}
          dataSource={rows}
          loading={isLoading}
          pagination={pagination}
          onChange={(next) => loadRows(next.current ?? 1, year)}
          locale={{ emptyText: <FormattedMessage id="agent.commissions.empty" /> }}
        />
      </div>
    </div>
  );
}
